import { cn } from "@apex/shared";
import { useWidget } from "../../state";
import type { ServiceUnavailableSubtype } from "../../state";

export interface ServiceUnavailableViewProps {
  className?: string;
}

const DEFAULT_MESSAGES: Record<ServiceUnavailableSubtype, string> = {
  subscription:
    "This service is temporarily unavailable. Please contact your account administrator.",
  origin:
    "This widget can't run on this site. The site owner has not authorised this domain.",
  key: "This widget is not configured correctly. Please contact the site owner.",
  "key-rotated": "This service has been updated. Please refresh the page.",
  "rate-limit": "Too many requests. Please try again in {retryAfter}.",
};

const DEFAULT_TITLE = "Service unavailable";

/**
 * Service-unavailable view (#554). Rendered when the widget API rejects
 * with a 403 (subscription_inactive / origin_not_allowed / key_invalid /
 * key_rotated) or a 429 (rate_limit_exceeded). Surfaces a tenant-themable
 * title + subtype-specific message + optional contact CTA + reference id.
 */
export function ServiceUnavailableView({ className }: ServiceUnavailableViewProps) {
  const { state, theme } = useWidget();
  const detail = state.serviceUnavailable;
  const subtype = detail?.subtype ?? "subscription";

  const message = resolveMessage(detail, theme.serviceUnavailableMessages);
  const websiteUrl = theme.tenantWebsiteUrl;
  const requestId = detail?.requestId;

  return (
    <div
      role="alert"
      data-subtype={subtype}
      className={cn(
        "apex-flex apex-flex-col apex-items-center apex-justify-center apex-min-h-[400px] apex-p-8 apex-text-center",
        className
      )}
    >
      {/* Lock icon */}
      <div className="apex-w-16 apex-h-16 apex-rounded-full apex-bg-muted apex-flex apex-items-center apex-justify-center apex-mb-6">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="apex-text-muted-foreground"
        >
          <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </div>

      <h2 className="apex-text-2xl apex-font-bold apex-text-foreground apex-mb-2">
        {DEFAULT_TITLE}
      </h2>

      <p className="apex-text-muted-foreground apex-mb-6 apex-max-w-md">{message}</p>

      {websiteUrl && (
        <a
          href={websiteUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="apex-inline-flex apex-items-center apex-gap-1 apex-text-sm apex-text-primary hover:apex-underline"
        >
          Contact us
        </a>
      )}

      {requestId && (
        <p className="apex-mt-4 apex-text-xs apex-text-muted-foreground">
          Reference: <code className="apex-font-mono">{requestId}</code>
        </p>
      )}
    </div>
  );
}

function resolveMessage(
  detail: { subtype: ServiceUnavailableSubtype; message?: string; retryAfter?: number } | null | undefined,
  overrides: Partial<Record<ServiceUnavailableSubtype, string>> | undefined
): string {
  if (!detail) return DEFAULT_MESSAGES.subscription;
  if (detail.message) return detail.message;
  const override = overrides?.[detail.subtype];
  if (override) {
    return interpolate(override, detail);
  }
  const fallback = DEFAULT_MESSAGES[detail.subtype];
  return interpolate(fallback, detail);
}

function interpolate(template: string, detail: { retryAfter?: number }): string {
  if (!template.includes("{retryAfter}")) return template;
  if (detail.retryAfter == null) {
    return template.replace("{retryAfter}", "a few minutes");
  }
  const seconds = Math.max(1, detail.retryAfter);
  if (seconds < 60) return template.replace("{retryAfter}", `${seconds}s`);
  const minutes = Math.ceil(seconds / 60);
  return template.replace("{retryAfter}", `${minutes} minute${minutes === 1 ? "" : "s"}`);
}
